import React, { Component } from 'react';
import styles from './style.module.css';
import { navigationHTML, navigationCSS, navigationJS } from '../../../../reducers';

export default class Header extends Component {
    state = {
        menuOpen: false,
        navHTML: null
    };

    // Coordination flags: the nav behaviour bundle (window.libroNav) loads
    // async, and the nav markup arrives async too. We init once both are ready.
    navScriptReady = false;
    navInited = false;

    componentDidMount() {
        // Pull in the main-site nav stylesheet (scoped under .libro-main-nav).
        if (!document.getElementById('libro-main-nav-css')) {
            const link = document.createElement('link');
            link.id = 'libro-main-nav-css';
            link.rel = 'stylesheet';
            link.href = navigationCSS;
            document.head.appendChild(link);
        }

        // Load the nav behaviour bundle (header + search) from the WP origin,
        // instead of copying the JS into this repo. Exposes window.libroNav.
        if (window.libroNav) {
            this.navScriptReady = true;
        } else if (!document.getElementById('libro-main-nav-js')) {
            const script = document.createElement('script');
            script.id = 'libro-main-nav-js';
            script.src = navigationJS;
            script.async = true;
            script.onload = () => { this.navScriptReady = true; this.tryInitNav(); };
            script.onerror = () => console.error('Failed to load main-site navigation script');
            document.body.appendChild(script);
        }

        fetch(navigationHTML)
            .then((res) => res.json())
            .then((data) => this.setState({ navHTML: data.html }))
            .catch((err) => console.error('Failed to load main-site navigation:', err));
    }

    componentDidUpdate(prevProps, prevState) {
        // Once the WP nav markup is injected, wire up its interactivity.
        if (!prevState.navHTML && this.state.navHTML) {
            this.tryInitNav();
            // Load Swiftype (Site Search) so the modal's autocomplete works, the
            // same engine the main site uses. Done AFTER injection so the
            // .st-default-search-input is in the DOM when st.js scans for it.
            this.loadSwiftype();
        }
    }

    // Runs exactly once, only after BOTH the bundle has loaded and the nav
    // markup is in the DOM (dangerouslySetInnerHTML doesn't run scripts, so the
    // handlers have to be attached here).
    tryInitNav() {
        if (this.navInited || !this.navScriptReady || !this.state.navHTML) return;
        if (!window.libroNav) return;
        this.navInited = true;
        try { window.libroNav.initHeader(); } catch (err) { console.warn('Main-nav header init failed:', err); }
        try { window.libroNav.initSearch(); } catch (err) { console.warn('Main-nav search init failed:', err); }
        // Wires up the alert banners' close buttons (and their dismissal cookie).
        try { window.libroNav.initAlerts(); } catch (err) { console.warn('Main-nav alerts init failed:', err); }
        // The nav's deferred images (e.g. the alert bell) carry only data-src;
        // WP's lazyload script isn't part of the injected markup, so resolve them here.
        try { window.libroNav.initLazyImages(); } catch (err) { console.warn('Main-nav lazy images failed:', err); }
        this.startSpacerSync();
    }

    // Alert banners render INSIDE the fixed #header, stacking on top of its
    // declared height. The main site absorbs that with a #main-content padding
    // rule keyed off #masthead, neither of which exists here — so publish the
    // measured height of the active banners and let the spacer add it on
    // (see .libro-main-nav-spacer in nav-standalone.scss).
    startSpacerSync() {
        this.syncAlertHeight();

        // Observing the banners covers all three ways the height changes: text
        // reflowing at a new viewport width, and the banner collapsing to
        // display:none when dismissed (which reports a 0-height box).
        const banners = Array.from(document.querySelectorAll('.libro-main-nav .alertBanner'));
        if (!banners.length) return;

        if (typeof window.ResizeObserver === 'function') {
            this.spacerObserver = new window.ResizeObserver(this.syncAlertHeight);
            banners.forEach((banner) => this.spacerObserver.observe(banner));
        } else {
            // Without ResizeObserver, resize covers reflow and the click
            // handler covers dismissal.
            window.addEventListener('resize', this.syncAlertHeight);
            banners.forEach((banner) => {
                const closeBtn = banner.querySelector('button');
                if (closeBtn) closeBtn.addEventListener('click', this.syncAlertHeight);
            });
            this.spacerFallbackBound = true;
        }
    }

    syncAlertHeight = () => {
        const active = Array.from(document.querySelectorAll('.libro-main-nav .alertBanner.active'));
        let total = 0;
        active.forEach((banner) => { total += banner.offsetHeight; });
        document.documentElement.style.setProperty('--libro-alert-height', `${total}px`);
    };

    componentWillUnmount() {
        if (this.spacerObserver) this.spacerObserver.disconnect();
        if (this.spacerFallbackBound) window.removeEventListener('resize', this.syncAlertHeight);
        document.documentElement.style.removeProperty('--libro-alert-height');
    }

    loadSwiftype() {
        if (window._st || document.getElementById('swiftype-install')) return;
        // Match the main site: production engine vs. dev engine.
        const engineKey = process.env.NODE_ENV === 'production'
            ? 'fKgYLzh7GYFsBfzSXiX2'
            : 'iS9KbnLacTJWQYiSozsg';
        (function (w, d, t, u, n, s, e) {
            w['SwiftypeObject'] = n;
            w[n] = w[n] || function () { (w[n].q = w[n].q || []).push(arguments); };
            s = d.createElement(t); s.id = 'swiftype-install'; s.async = 1; s.src = u;
            e = d.getElementsByTagName(t)[0]; e.parentNode.insertBefore(s, e);
        })(window, document, 'script', 'https://s.swiftypecdn.com/install/v2/st.js', '_st');
        window._st('install', engineKey, '2.0.0');
    }

    toggleMenu = () => {
        const {
            state: {
                menuOpen
            }
        } = this;

        this.setState({
            menuOpen: !menuOpen
        });
    };

    render(){
        const {
            props: {
                navigation
            },
            state: {
                menuOpen,
                navHTML
            }
        } = this;

        // TEST: render the main libro.ca header markup fetched from WP.
        // The .libro-main-nav wrapper scopes the ported stylesheet so it
        // doesn't leak onto the calculators' own UI.
        if (navHTML) {
            return (
                <React.Fragment>
                    <div
                        className='libro-main-nav'
                        dangerouslySetInnerHTML={{ __html: navHTML }}
                    />
                    {/* Reserves space for the fixed header so it doesn't overlap content. */}
                    <div className='libro-main-nav-spacer' />
                </React.Fragment>
            );
        }

        const navigationCondition = navigation.length > 1;

        const navigationItems = navigation.map((navItem) => (
            <li key={navItem.path}>
                <a
                    href={process.env.PUBLIC_URL + navItem.path}
                    title={navItem.name}
                >
                    {navItem.name}
                </a>
            </li>
        ));

        return(
            <div className={styles.header}>
                <div className={styles.container}>
                    <a href='https://www.libro.ca/' title='Libro Website' className={styles.logo}>
                        <img
                            src={`${window.PUBLIC_URL}/images/logo-libro-white.svg`}
                            alt='Libro Logo'
                        />
                    </a>
                    {
                        navigationCondition &&
                        [
                            <button
                                key='menu'
                                onClick={this.toggleMenu}
                                className={menuOpen ? styles.menuButtonOpen : styles.menuButton}
                                aria-controls='header'
                                title='Open Menu'
                            >
                                <div className={styles.line1}/>
                                <div className={styles.line2}/>
                                <div className={styles.line3}/>
                                <span className='invisible'>Open Menu</span>
                            </button>,
                            <ul key='navigation' className={styles.navigation}>
                                {navigationItems}
                            </ul>
                        ]
                    }
                </div>
                {
                    navigationCondition &&
                    <ul className={menuOpen ? styles.navigationMobileOpen : styles.navigationMobile}>
                        {navigationItems}
                    </ul>
                }
            </div>
        );
    }
}
